'use client';

import { c as _c } from "react/compiler-runtime";
import { Tabs, TabsContent } from '@achra/ui/tabs';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import { Suspense, useCallback } from 'react';
import type { PurchaseResourceTemplateFieldsFragment, ResourceOperatorFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import { SERVICE_PURCHASE_STEP_VALUES, SERVICE_PURCHASE_STEPS_ENTRIES } from '@/modules/service-purchase/lib/constants';
import { useServicePurchaseStep } from '@/modules/service-purchase/providers/service-purchase-store-provider';
import { ServicePurchaseStep } from '@/modules/service-purchase/types';
import { BookCallButton, ServiceInfo } from '@/modules/services/components/service-info';
import { ErrorBoundaryWithPresets } from '@/modules/shared/components/error-state';
import { ConfigureServices, ConfigureServicesSkeleton } from '../configure-services-step/configure-services';
import { ConfirmationStep } from '../confirmation-step';
import { NavigationButtons } from '../navigation-buttons';
import { ProductInfo } from '../product-info';
import { SelectOperatorAction } from '../select-operator-action';
import { SelectOperatorStep } from '../select-operator-step';
import { SummaryStep } from '../summary-step';
import { OperatorBadge } from './operator-badge';
import { StepsTriggersList } from './steps-trigger/steps-triggers-list';
export interface ServicePurchaseWizardProps {
  resourceTemplate: PurchaseResourceTemplateFieldsFragment;
  operator: ResourceOperatorFieldsFragment;
}
function ServicePurchaseWizard(t0) {
  const $ = _c(38);
  const {
    resourceTemplate,
    operator
  } = t0;
  const {
    activeStep,
    goToStep
  } = useServicePurchaseStep();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsStringEnum(SERVICE_PURCHASE_STEP_VALUES);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [urlStep] = useQueryState("step", t1);
  const isProductInfo = (urlStep ?? activeStep) === ServicePurchaseStep.ProductInfo;
  let t2;
  if ($[1] !== goToStep) {
    t2 = _operatorId => {
      goToStep(ServicePurchaseStep.ConfigureServices);
    };
    $[1] = goToStep;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleOnSelectOperator = t2;
  let t3;
  if ($[3] !== goToStep) {
    t3 = value => {
      goToStep(value as ServicePurchaseStep);
    };
    $[3] = goToStep;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleTabsNavigation = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <StepsTriggersList />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = !isProductInfo;
  let t6;
  if ($[6] !== activeStep || $[7] !== operator) {
    t6 = ![ServicePurchaseStep.ProductInfo, ServicePurchaseStep.SelectOperator].includes(activeStep) ? <OperatorBadge operator={operator} /> : undefined;
    $[6] = activeStep;
    $[7] = operator;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isProductInfo) {
    t7 = isProductInfo ? <SelectOperatorAction /> : undefined;
    $[9] = isProductInfo;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <BookCallButton />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== resourceTemplate.id || $[13] !== resourceTemplate.summary || $[14] !== resourceTemplate.thumbnailUrl || $[15] !== resourceTemplate.title || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t9 = <div className="min-w-0 flex-1"><ServiceInfo id={resourceTemplate.id} isCompacted={t5} title={resourceTemplate.title} summary={resourceTemplate.summary} thumbnailUrl={resourceTemplate.thumbnailUrl} operator={t6} actions={t7}>{t8}</ServiceInfo></div>;
    $[12] = resourceTemplate.id;
    $[13] = resourceTemplate.summary;
    $[14] = resourceTemplate.thumbnailUrl;
    $[15] = resourceTemplate.title;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isProductInfo) {
    t10 = !isProductInfo && <div className="shrink-0"><NavigationButtons /></div>;
    $[20] = isProductInfo;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <div className="flex flex-col gap-4 sm:flex-row sm:items-center">{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== handleOnSelectOperator || $[26] !== operator || $[27] !== resourceTemplate.contentSections || $[28] !== resourceTemplate.description || $[29] !== resourceTemplate.faqFields || $[30] !== resourceTemplate.subtitle || $[31] !== resourceTemplate.title) {
    t12 = SERVICE_PURCHASE_STEPS_ENTRIES.map(step => <TabsContent key={step.value} value={step.value} className="m-0 flex flex-col gap-2">{step.value === ServicePurchaseStep.ProductInfo && <ProductInfo description={resourceTemplate.description} contentSections={resourceTemplate.contentSections} faqFields={resourceTemplate.faqFields} />}{step.value === ServicePurchaseStep.SelectOperator && <SelectOperatorStep onSelectOperator={handleOnSelectOperator} operator={operator} />}{step.value === ServicePurchaseStep.ConfigureServices && <ErrorBoundaryWithPresets description="We ran into an unexpected error while loading the service configuration. Please try again later."><Suspense fallback={<ConfigureServicesSkeleton />}><ConfigureServices /></Suspense></ErrorBoundaryWithPresets>}{step.value === ServicePurchaseStep.Summary && <SummaryStep templateTitle={resourceTemplate.title} templateSubtitle={resourceTemplate.subtitle} />}{step.value === ServicePurchaseStep.Confirmation && <ConfirmationStep />}</TabsContent>);
    $[25] = handleOnSelectOperator;
    $[26] = operator;
    $[27] = resourceTemplate.contentSections;
    $[28] = resourceTemplate.description;
    $[29] = resourceTemplate.faqFields;
    $[30] = resourceTemplate.subtitle;
    $[31] = resourceTemplate.title;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== activeStep || $[34] !== handleTabsNavigation || $[35] !== t11 || $[36] !== t12) {
    t13 = <div className="flex flex-col gap-6 lg:gap-8"><Tabs value={activeStep} onValueChange={handleTabsNavigation} className="w-full gap-8">{t4}{t11}{t12}</Tabs></div>;
    $[33] = activeStep;
    $[34] = handleTabsNavigation;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}
export { ServicePurchaseWizard };